/**
 * The rental module's API (/api/v1/rental): stock, planning and purchasing. Amounts are in cents,
 * VAT rates in basis points (2100 = 21 %), moments ISO strings in UTC (typed and shown in the
 * company's zone, see zoned.ts), days YYYY-MM-DD (a delivery day: no time, no zone).
 */
import { api } from './api'

type Body = Record<string, unknown>
const data = <T>(p: Promise<{ data: { data: T } }>) => p.then(r => r.data.data)

export type Tracking = 'ITEM' | 'BULK'
/** plain: its own stock; kit: made of other articles when rented; container: a box whose pieces hold other articles */
export type ArticleKind = 'PLAIN' | 'KIT' | 'CONTAINER'
export type ItemStatus = 'IN_STOCK' | 'AT_EVENT' | 'IN_REPAIR' | 'LOST' | 'RETIRED' | 'RETURNED'
export type EventStatus = 'REQUESTED' | 'RESERVED' | 'CONFIRMED' | 'CLOSED' | 'CANCELLED'
export type SubRentalStatus = 'DRAFT' | 'AWAITING_APPROVAL' | 'SENT' | 'CONFIRMED' | 'CLOSED' | 'CANCELLED'
export type PurchaseOrderStatus = 'DRAFT' | 'AWAITING_APPROVAL' | 'SENT' | 'PARTLY_RECEIVED' | 'RECEIVED' | 'CANCELLED'
export type Lang = 'en' | 'nl' | 'fr' | 'de' | 'es'

export interface RentalSettings {
  pricingMethod: 'STAFFEL' | 'FIXED'
  staffel: { days: number; factor: number }[]
  extraDayFactor: number
  vatRate: number
  currency: string
  fourEyes: boolean
  /** an event site is removed this many days after its event closed */
  eventSiteArchiveDays: number
  letterName: string | null; letterAddress: string | null; letterVatNumber: string | null; letterCocNumber: string | null; letterEmail: string | null; letterPhone: string | null
  timezone: string
}
export interface Warehouse { id: string; name: string; address: string | null; isDefault: boolean }
export interface Category { id: string; name: string; sortOrder: number }
export interface Article {
  id: string; sku: string; name: string; description: string | null; categoryId: string | null; kind: ArticleKind; tracking: Tracking; active: boolean
  dayPrice: number; weekendPrice: number | null; weekPrice: number | null; vatRate: number | null; purchasePrice?: number | null; externalCode: string | null
  stock: { warehouseId: string; owned: number }[]
}
/** the dashboard device an item is tied to; hidden when it is on a site the person may not see */
export type ItemDevice = { kind: 'site' | 'unifi'; id: string; name: string; status: string; lastSeenAt: string | null; siteId: string; siteName: string; siteProvider: string }
  /** a Starlink terminal: on its site, or on none */
  | { kind: 'starlink'; id: string; name: string; status: string; lastSeenAt: string | null; siteId: string | null; siteName: string | null; siteProvider: null }
  | { hidden: true }
/** an item of a container article is a piece: its code is its serial number */
export interface Item {
  id: string; articleId: string; serialNumber: string | null; mac: string | null; tag: string | null; warehouseId: string; status: ItemStatus
  notes: string | null; containerId: string | null; subRentalId: string | null; deviceSiteId: string | null; unifiDeviceId: string | null
  article: { id: string; sku: string; name: string; kind: ArticleKind }
  container: { id: string; serialNumber: string | null; article: { id: string; sku: string; name: string } } | null
  device: ItemDevice | null
  /** the event it is out at (itself, or in a piece that is) */
  event: { id: string; name: string } | null
}
/** per warehouse: the count (with what is out at events), what pieces here hold, what is out at events */
export interface Bulk { warehouseId: string; quantity: number; packed: number; out: number }
export interface Part { partId: string; quantity: number; part: { id: string; sku: string; name: string; kind: ArticleKind; tracking: Tracking } }
export interface Count { articleId: string; quantity: number }
/** what a piece holds, and whether that is what its container's recipe says */
export interface PieceContents {
  piece: Item; items: Item[]
  bulk: { articleId: string; quantity: number; article: { id: string; sku: string; name: string } }[]
  check: { complete: boolean; missing: Count[]; extra: Count[] }
}
export interface DeviceHit {
  kind: 'site' | 'unifi' | 'starlink'; id: string; name: string; serial: string | null; mac: string | null; model: string | null
  /** a Starlink terminal on no site: none */
  status: string; lastSeenAt: string | null; siteId: string | null; siteName: string | null; itemId: string | null
}
/** the brands of the dashboard's devices (their sites' licence modules, and Starlink's terminals) */
export const DEVICE_BRANDS = ['mikrotik', 'unifi', 'teltonika', 'peplink', 'velanox', 'omada', 'ruckus', 'starlink'] as const
export type DeviceBrand = typeof DEVICE_BRANDS[number]
export interface BrowseHit extends DeviceHit { brand: DeviceBrand }
/** an event's devices over a range: each point the average of its step (seconds) */
export interface EventGraph {
  from: string; to: string; step: number
  points: { at: string; online: number; offline: number; rxBytesPerSec: number; txBytesPerSec: number }[]
  totalBytes: { rx: number; tx: number }
}
export interface Transfer {
  id: string; fromWarehouseId: string; toWarehouseId: string; note: string | null; createdAt: string; byName: string | null
  lines: { id: string; itemId: string | null; articleId: string | null; quantity: number | null }[]
}
export interface HistoryEntry {
  id: string; at: string; action: string; byName: string | null; itemId: string | null; pieceId: string | null; articleId: string | null
  warehouseId: string | null; eventId: string | null; from: string | null; to: string | null; quantity: number | null; note: string | null
}
export interface Customer {
  id: string; name: string; contactName: string | null; email: string | null; phone: string | null; address: string | null; vatNumber: string | null
  externalRef: string | null; notes: string | null; _count?: { events: number }
  events?: { id: string; name: string; status: EventStatus; rentalStart: string; rentalEnd: string }[]
}
export interface Supplier {
  id: string; name: string; contactName: string | null; email: string | null; phone: string | null; address: string | null; vatNumber: string | null
  language: Lang; notes: string | null; _count?: { purchaseOrders: number; subRentals: number }
  purchaseOrders?: { id: string; number: string; status: PurchaseOrderStatus; expectedOn: string; createdAt: string }[]
  subRentals?: { id: string; number: string; status: SubRentalStatus; from: string; to: string; createdAt: string }[]
}

export interface RentalEvent {
  id: string; name: string; status: EventStatus; customerId: string; warehouseId: string; siteId: string | null
  rentalStart: string; rentalEnd: string; materialOut: string; materialBack: string
  discountPercent: number | null; discountAmount: number | null; externalRef: string | null; notes: string | null
  reservedAt: string | null; confirmedAt: string | null; closedAt: string | null; cancelledAt: string | null
  customer: { id: string; name: string }; warehouse: { id: string; name: string }; site: { id: string; name: string } | null
  /** its client people may export it from their page (Export on every page) */
  clientExportAllowed: boolean
}
/** what a line needs and finds of one article */
export interface Figures {
  need: number; short: number; freeCountingOffers: number; freeIfOffersFall: number; subrented: number; expected: number; requested: number
  elsewhere: { warehouseId: string; free: number }[]
}
/** a part of a kit line: `per` kit, `need` for the whole line */
export interface LinePart extends Figures { articleId: string; sku: string; name: string; per: number }
export interface EventLine extends Figures {
  id: string; articleId: string; quantity: number; note: string | null; customUnitPrice: number | null; sortOrder: number
  article: { id: string; sku: string; name: string; tracking: Tracking; active: boolean }
  calculatedUnitPrice: number; unitPrice: number; vatRate: number; total: number; discountShare: number
  /** a kit line: what it holds of each part (its own figures are in kits) */
  parts?: LinePart[]
  /** a container line: how many of the free pieces in its warehouse are incomplete */
  incomplete?: number
}
export interface Totals { subtotal: number; discount: number; vat: { rate: number; amount: number }[]; total: number }
/** the event's comment: changed by everyone who sees the event, with who changed it last and when */
export interface EventComment { comment: string | null; commentAt: string | null; commentBy: { name: string } | null }
export interface EventDetail extends RentalEvent, EventComment {
  lines: EventLine[]; totals: Totals
  /** how often the customer's devices are pinged while it is on (ping checks), who changed it last */
  pingMode: PingMode; pingModeAt: string | null; pingModeBy: string | null
  /** when it may be removed: a cancelled event at once, a closed one after the company's keep time; null while it runs */
  removableFrom: string | null
}
/** what went out on a line and how it came back: an item or piece, or a quantity of bulk */
export interface LoadRow {
  id: string; lineId: string
  item: { id: string; name: string; serialNumber: string | null; tag: string | null; isPiece: boolean } | null
  article: { id: string; name: string; sku: string }
  quantity: number | null
  loadedAt: string; loadedBy: string | null
  returnedAt: string | null; returnedBy: string | null; returnedAs: ItemStatus | null; returnedQuantity: number | null
}
export type ReturnAs = 'IN_STOCK' | 'IN_REPAIR' | 'LOST'
/** an event a phone remembers for scanning offline */
export type ScanEvent = { id: string; name: string; customer: string; materialOut: string; materialBack: string }
export type TagHit = { companyId: string; itemId: string; articleId: string; isPiece: boolean } | { companyId: string }
/** a phone's scan: its id (a resend gets the same answer) and when it was made */
type ScanFrom = { scanId?: string; scannedAt?: string }
export interface EventRow extends RentalEvent { lineCount: number; total: number; short: number }
export interface PlanningDay { date: string; owned: number; subrented: number; expected: number; held: number; free: number; short: number; moments: { at: string; held: number; free: number; short: number }[] }
export interface Planning {
  days: string[]
  /** a kit's days come from its parts: no stock of its own and no moments */
  articles: { id: string; sku: string; name: string; categoryId: string | null; tracking: Tracking; kind: ArticleKind; days: PlanningDay[] }[]
  shortEvents: { id: string; name: string; status: EventStatus; customer: { id: string; name: string }; materialOut: string; materialBack: string; short: number }[]
}
export type OrderKind = 'purchase' | 'subrental'
interface OrderBase {
  id: string; number: string; supplierId: string; warehouseId: string; notes: string | null
  createdById: string | null; requestedById: string | null; requestedAt: string | null; approvedById: string | null; approvedAt: string | null
  sentAt: string | null; sentTo: string | null; sentById: string | null; sendError: string | null; createdAt: string
  createdByName?: string | null; requestedByName?: string | null; approvedByName?: string | null; sentByName?: string | null
  supplier: { id: string; name: string; email: string | null; language: Lang }; warehouse: { id: string; name: string }
  total?: number
}
export interface OrderLine {
  id: string; articleId: string; supplierCode: string | null; quantity: number; received?: number; unitPrice?: number; unitCost?: number; total?: number
  article: { id: string; sku: string; name: string; tracking: Tracking }
}
/** `expectedOn` is the day it should arrive (YYYY-MM-DD): it counts from the start of that day */
export interface PurchaseOrder extends OrderBase { status: PurchaseOrderStatus; expectedOn: string; lines: OrderLine[] }
export interface SubRental extends OrderBase {
  status: SubRentalStatus; from: string; to: string; eventId: string | null; event: { id: string; name: string } | null
  confirmedAt: string | null; closedAt: string | null; lines: OrderLine[]
  items: { id: string; articleId: string; serialNumber: string | null; mac: string | null; tag: string | null; status: ItemStatus }[]
}
export type OrderAction = 'request-approval' | 'approve' | 'send' | 'mark-sent' | 'cancel' | 'confirm' | 'close'
const path = (kind: OrderKind) => (kind === 'purchase' ? 'purchase-orders' : 'subrentals')

export const rentalApi = {
  settings: () => data<RentalSettings>(api.get('/rental/settings')),
  saveSettings: (b: Body) => data<RentalSettings>(api.put('/rental/settings', b)),

  warehouses: () => data<Warehouse[]>(api.get('/rental/warehouses')),
  createWarehouse: (b: Body) => data<Warehouse>(api.post('/rental/warehouses', b)),
  updateWarehouse: (id: string, b: Body) => data<Warehouse>(api.put(`/rental/warehouses/${id}`, b)),
  deleteWarehouse: (id: string) => api.delete(`/rental/warehouses/${id}`),

  categories: () => data<Category[]>(api.get('/rental/categories')),
  createCategory: (b: Body) => data<Category>(api.post('/rental/categories', b)),
  updateCategory: (id: string, b: Body) => data<Category>(api.put(`/rental/categories/${id}`, b)),
  deleteCategory: (id: string) => api.delete(`/rental/categories/${id}`),

  articles: (q: { q?: string; categoryId?: string; active?: boolean } = {}) =>
    data<Article[]>(api.get('/rental/articles', { params: { ...q, active: q.active === undefined ? undefined : String(q.active) } })),
  article: (id: string) => data<Article>(api.get(`/rental/articles/${id}`)),
  createArticle: (b: Body) => data<Article>(api.post('/rental/articles', b)),
  updateArticle: (id: string, b: Body) => data<Article>(api.put(`/rental/articles/${id}`, b)),
  deleteArticle: (id: string) => api.delete(`/rental/articles/${id}`),
  parts: (id: string) => data<Part[]>(api.get(`/rental/articles/${id}/parts`)),
  setParts: (id: string, parts: { partId: string; quantity: number }[]) => data<Part[]>(api.put(`/rental/articles/${id}/parts`, parts)),
  /** the next free piece code of a container (SKU-0001, SKU-0002, …) */
  nextCode: (id: string) => data<{ code: string }>(api.get(`/rental/articles/${id}/next-code`)).then(r => r.code),

  /** retired items only with `retired` (or a status asked for) */
  items: ({ retired, ...q }: { articleId?: string; warehouseId?: string; status?: ItemStatus; containerId?: string; kind?: ArticleKind; retired?: boolean } = {}) =>
    data<Item[]>(api.get('/rental/items', { params: { ...q, ...(retired ? { retired: '1' } : {}) } })),
  createItem: (articleId: string, b: Body) => data<Item>(api.post(`/rental/articles/${articleId}/items`, b)),
  updateItem: (id: string, b: Body) => data<Item>(api.put(`/rental/items/${id}`, b)),
  /** taken out of the inventory: deleted when it never went out to an event, else retired (its history kept) */
  removeItem: (id: string) => data<{ removed: 'deleted' | 'retired' }>(api.delete(`/rental/items/${id}`)),

  bulk: (articleId: string) => data<Bulk[]>(api.get(`/rental/articles/${articleId}/bulk`)),
  countBulk: (articleId: string, b: Body) => data<Bulk[]>(api.put(`/rental/articles/${articleId}/bulk`, b)),

  contents: (pieceId: string) => data<PieceContents>(api.get(`/rental/items/${pieceId}/contents`)),
  /** what the piece holds afterwards: `items` and `bulk` each replace what it held */
  setContents: (pieceId: string, b: { items?: string[]; bulk?: { articleId: string; quantity: number }[] }) => data<PieceContents>(api.put(`/rental/items/${pieceId}/contents`, b)),
  /** a dashboard device into a piece; `articleId` makes its item when it is not in the inventory yet */
  addDeviceToPiece: (pieceId: string, b: { siteId?: string; unifiDeviceId?: string; articleId?: string }) => data<PieceContents>(api.post(`/rental/items/${pieceId}/contents/device`, b)),

  devices: (q: string) => data<DeviceHit[]>(api.get('/rental/devices', { params: { q } })),
  /** a brand's devices (or every licensed brand's) whose name, serial or MAC holds q: at most 500, with the total */
  browseDevices: (f: { brand?: DeviceBrand; q?: string }) => data<{ total: number; devices: BrowseHit[] }>(api.get('/rental/devices/browse', { params: f })),
  /** items of an article made from devices, each tied to its own: all or none */
  addItemsFromDevices: (articleId: string, b: { warehouseId: string; devices: ({ siteId: string } | { unifiDeviceId: string } | { starlinkTerminalId: string })[] }) =>
    data<Item[]>(api.post(`/rental/articles/${articleId}/items/from-devices`, b)),
  tieDevice: (itemId: string, b: { siteId?: string; unifiDeviceId?: string }) => data<Item>(api.put(`/rental/items/${itemId}/device`, b)),
  untieDevice: (itemId: string) => data<Item>(api.delete(`/rental/items/${itemId}/device`)),

  transfers: () => data<Transfer[]>(api.get('/rental/transfers')),
  createTransfer: (b: Body) => data<Transfer>(api.post('/rental/transfers', b)),

  history: (q: { itemId?: string; pieceId?: string; articleId?: string; eventId?: string }) => data<HistoryEntry[]>(api.get('/rental/history', { params: q })),

  customers: (q?: string) => data<Customer[]>(api.get('/rental/customers', { params: { q } })),
  customer: (id: string) => data<Customer>(api.get(`/rental/customers/${id}`)),
  createCustomer: (b: Body) => data<Customer>(api.post('/rental/customers', b)),
  updateCustomer: (id: string, b: Body) => data<Customer>(api.put(`/rental/customers/${id}`, b)),
  deleteCustomer: (id: string) => api.delete(`/rental/customers/${id}`),

  suppliers: (q?: string) => data<Supplier[]>(api.get('/rental/suppliers', { params: { q } })),
  supplier: (id: string) => data<Supplier>(api.get(`/rental/suppliers/${id}`)),
  createSupplier: (b: Body) => data<Supplier>(api.post('/rental/suppliers', b)),
  updateSupplier: (id: string, b: Body) => data<Supplier>(api.put(`/rental/suppliers/${id}`, b)),
  deleteSupplier: (id: string) => api.delete(`/rental/suppliers/${id}`),

  events: (q: { status?: EventStatus; customerId?: string; from?: string; to?: string; q?: string } = {}) => data<EventRow[]>(api.get('/rental/events', { params: q })),
  event: (id: string) => data<EventDetail>(api.get(`/rental/events/${id}`)),
  /** whether the event's client people may export it from their page */
  setClientExport: (id: string, allowed: boolean) => data<{ clientExportAllowed: boolean }>(api.put(`/rental/events/${id}/client-export`, { allowed })),
  createEvent: (b: Body) => data<RentalEvent>(api.post('/rental/events', b)),
  updateEvent: (id: string, b: Body) => data<EventDetail>(api.put(`/rental/events/${id}`, b)),
  setEventComment: (id: string, comment: string) => data<EventComment>(api.put(`/rental/events/${id}/comment`, { comment })),
  /** the event's graphs for a range: samples averaged into at most 300 points, the range's total traffic */
  eventGraph: (eventId: string, from: Date, to: Date) => data<EventGraph>(api.get(`/rental/events/${eventId}/graph`, { params: { from: from.toISOString(), to: to.toISOString() } })),
  /** where a device stands at the event (empty: removed) */
  setDeviceNote: (eventId: string, itemId: string, note: string) => data<unknown>(api.put(`/rental/events/${eventId}/devices/${itemId}/note`, { note })),
  /** the event with all that is only its (a cancelled one, or a closed one after the keep time) */
  removeEvent: (id: string) => api.delete(`/rental/events/${id}`),
  setEventStatus: (id: string, status: EventStatus) => data<RentalEvent>(api.post(`/rental/events/${id}/status`, { status })),
  addLine: (eventId: string, b: Body) => data<EventLine>(api.post(`/rental/events/${eventId}/lines`, b)),
  updateLine: (eventId: string, lineId: string, b: Body) => data<EventLine>(api.put(`/rental/events/${eventId}/lines/${lineId}`, b)),
  deleteLine: (eventId: string, lineId: string) => api.delete(`/rental/events/${eventId}/lines/${lineId}`),
  // loading a confirmed event, and returning (loading.ts)
  loads: (eventId: string) => data<LoadRow[]>(api.get(`/rental/events/${eventId}/loads`)),
  load: (eventId: string, b: { lineId: string; itemId: string } | { lineId: string; quantity: number; articleId?: string } | ({ scan: string } & ScanFrom), o?: { timeout: number }) =>
    data<LoadRow>(api.post(`/rental/events/${eventId}/loads`, b, o)),
  returnLoad: (eventId: string, loadId: string, b: { as?: ReturnAs; quantity?: number; lost?: string[] }) =>
    data<LoadRow>(api.post(`/rental/events/${eventId}/loads/${loadId}/return`, b)),
  returnScan: (eventId: string, b: { scan: string; as?: ReturnAs } & ScanFrom, o?: { timeout: number }) => data<LoadRow>(api.post(`/rental/events/${eventId}/returns`, b, o)),
  scanEvents: () => data<ScanEvent[]>(api.get('/rental/scan-events')),
  /** a tag's address: this company's item, or which other company of the person has it */
  findTag: (code: string) => data<TagHit>(api.get(`/tags/${encodeURIComponent(code)}`)),
  planning: (q: { from: string; to: string; warehouseId?: string; categoryId?: string }) => data<Planning>(api.get('/rental/planning', { params: q })),

  orders: <K extends OrderKind>(kind: K, q: { status?: string; supplierId?: string; eventId?: string } = {}) =>
    data<K extends 'purchase' ? PurchaseOrder[] : SubRental[]>(api.get(`/rental/${path(kind)}`, { params: q })),
  order: <K extends OrderKind>(kind: K, id: string) => data<K extends 'purchase' ? PurchaseOrder : SubRental>(api.get(`/rental/${path(kind)}/${id}`)),
  createOrder: (kind: OrderKind, b: Body) => data<{ id: string }>(api.post(`/rental/${path(kind)}`, b)),
  updateOrder: (kind: OrderKind, id: string, b: Body) => data<{ id: string }>(api.put(`/rental/${path(kind)}/${id}`, b)),
  deleteOrder: (kind: OrderKind, id: string) => api.delete(`/rental/${path(kind)}/${id}`),
  /** mark-sent: the letter went to the supplier by hand */
  orderAction: (kind: OrderKind, id: string, action: OrderAction) => data<{ id: string; status: string; sendError: string | null }>(api.post(`/rental/${path(kind)}/${id}/${action}`, {})),
  receive: (id: string, b: Body) => data<PurchaseOrder>(api.post(`/rental/purchase-orders/${id}/receive`, b)),
  registerItems: (id: string, items: Body[]) => data<SubRental>(api.post(`/rental/subrentals/${id}/items`, items)),
  /** the letter as the supplier gets it */
  pdf: (kind: OrderKind, id: string) => api.get(`/rental/${path(kind)}/${id}/pdf`, { responseType: 'blob' }).then(r => r.data as Blob),
}

// ─── client people and the client view (the portal, and "View as client") ───────

/** one of a customer's client people */
export interface ClientPerson {
  id: string; name: string; email: string; isMainContact: boolean; onlyDuringEvent: boolean
  invitedBy: string | null; createdAt: string
  /** the account has a password: the invitation was taken up */
  accountReady: boolean
}
export interface ClientInvite { email: string; name?: string; onlyDuringEvent?: boolean; isMainContact?: boolean }
/** a customer's event as its client people see it */
export interface PortalEventRow { id: string; name: string; rentalStart: string; rentalEnd: string; materialOut: string; materialBack: string; status: EventStatus }
export interface PortalEventDetail extends PortalEventRow {
  comment: string | null; commentAt: string | null; commentBy: string | null
  /** the company allows its client people to export it (the portal's own copy only) */
  exportAllowed?: boolean
}
/** "View as client": what the event's customer's client people see, opened on this event */
export interface ClientView {
  customer: { id: string; name: string }; invited: boolean; events: PortalEventRow[]
  /** the event is one the client does not see */
  hiddenBecause: 'REQUESTED' | 'CANCELLED' | null
  event: PortalEventDetail | null; view: import('../pages/sites/EventSiteDetail').EventSiteView | null; people: ClientPerson[]
}
export interface PortalMe {
  clientPersonId: string; name: string; email: string; isMainContact: boolean; onlyDuringEvent: boolean
  /** a push when the event's equipment goes offline (the app) */
  pushAlerts: boolean
  /** a push when one of the customer's pinged devices stops answering (the app) */
  pushPings: boolean
  /** the customer has pinged devices (and the company ping checks): the switch and the card are there */
  hasPings: boolean
  company: { id: string; name: string; slug: string }; customer: { id: string; name: string }; timezone: string
}

/** a device out on the client's event, as the client may see it (docs/superpowers/specs/2026-11-02-client-device-pages-design.md) */
export type ClientDeviceKind = 'mikrotik' | 'teltonika' | 'peplink' | 'velanox' | 'device' | 'site'
export interface ClientDeviceView {
  itemId: string; name: string; kind: ClientDeviceKind; provider: string; status: string; since: string; exportAllowed: boolean
  /** the brand page's data, without anything hidden from a client */
  page: unknown
  alerts: { level: string; title: string; message: string; createdAt: string; resolvedAt: string | null }[]
}
export type ClientDevicePeriod = '1h' | '6h' | '24h' | '7d'
export interface ClientDeviceHistory { from: string; to: string; points: { at: string; rxBytesPerSec: number; txBytesPerSec: number; clients: number | null }[] }
export interface ClientDeviceClient { hostname: string | null; ipAddress: string | null; macAddress: string; ssid: string | null; rxBytesPerSec: number; txBytesPerSec: number; lastSeenAt: string | null }

// ─── ping checks (docs/superpowers/specs/2026-11-02-ping-checks-design.md) ─────────

export const PING_MODES = ['EVERY_30S', 'EVERY_MINUTE', 'EVERY_5_MINUTES', 'ON_OPEN', 'OFF'] as const
export type PingMode = typeof PING_MODES[number]
export type PingStatus = 'ONLINE' | 'OFFLINE' | 'NO_ANSWER' | 'UNKNOWN'
/** one of a customer's devices, pinged at its IP through the company's MikroTiks */
export interface PingTarget {
  id: string; name: string; ip: string; status: PingStatus
  /** pinged now: one of the customer's events on, its setting not off */
  active: boolean
  rttMs: number | null; answeredAt: string | null; checkedAt: string | null
  /** the MikroTik it answered through (the client: its name only; a member without access to that site: hidden) */
  foundThrough: { siteId?: string; name: string; hidden?: false } | { hidden: true } | null
}
export interface PortalPings {
  /** this event's setting (the main contact's choice), who changed it last */
  mode: PingMode; modeAt: string | null; modeBy: string | null
  /** the setting the devices are checked under now: the most frequent of the customer's running events; null: none on */
  checkedAs: PingMode | null
  mayChange: boolean; targets: PingTarget[]
}
type PingSetting = { pingMode: PingMode; pingModeAt: string | null; pingModeBy: string | null }

export const pingsApi = {
  list: (customerId: string) => data<PingTarget[]>(api.get(`/rental/customers/${customerId}/pings`)),
  add: (customerId: string, body: { name: string; ip: string }) => data<PingTarget>(api.post(`/rental/customers/${customerId}/pings`, body)),
  update: (customerId: string, id: string, body: { name?: string; ip?: string }) => data<PingTarget>(api.put(`/rental/customers/${customerId}/pings/${id}`, body)),
  remove: (customerId: string, id: string) => api.delete(`/rental/customers/${customerId}/pings/${id}`).then(() => null),
  ping: (customerId: string, id: string) => data<PingTarget>(api.post(`/rental/customers/${customerId}/pings/${id}/ping`)),
  setMode: (eventId: string, mode: PingMode) => data<PingSetting>(api.put(`/rental/events/${eventId}/ping-mode`, { mode })),
}

export const clientPeopleApi = {
  list: (customerId: string) => data<ClientPerson[]>(api.get(`/rental/customers/${customerId}/people`)),
  invite: (customerId: string, body: ClientInvite) => data<ClientPerson & { emailSent: boolean }>(api.post(`/rental/customers/${customerId}/people`, body)),
  update: (customerId: string, id: string, body: { onlyDuringEvent?: boolean; isMainContact?: boolean }) => data<ClientPerson>(api.put(`/rental/customers/${customerId}/people/${id}`, body)),
  remove: (customerId: string, id: string) => data<null>(api.delete(`/rental/customers/${customerId}/people/${id}`)),
  clientView: (eventId: string) => data<ClientView>(api.get(`/rental/events/${eventId}/client-view`)),
  previewDevice: (eventId: string, itemId: string) => data<ClientDeviceView>(api.get(`/rental/events/${eventId}/client-view/devices/${itemId}`)),
  previewDeviceHistory: (eventId: string, itemId: string, period: ClientDevicePeriod) => data<ClientDeviceHistory>(api.get(`/rental/events/${eventId}/client-view/devices/${itemId}/history`, { params: { period } })),
  previewDeviceClients: (eventId: string, itemId: string) => data<ClientDeviceClient[]>(api.get(`/rental/events/${eventId}/client-view/devices/${itemId}/clients`)),
  previewPings: (eventId: string) => data<PortalPings>(api.get(`/rental/events/${eventId}/client-view/pings`)),
}

/** the portal (/api/v1/portal): client people only */
export const portalApi = {
  me: () => data<PortalMe>(api.get('/portal/me')),
  setPush: (on: boolean) => data<{ pushAlerts: boolean }>(api.put('/portal/me/push', { on })),
  setPushPings: (on: boolean) => data<{ pushPings: boolean }>(api.put('/portal/me/push-pings', { on })),
  pings: (eventId: string) => data<PortalPings>(api.get(`/portal/events/${eventId}/pings`)),
  pingNow: (eventId: string, id: string) => data<PingTarget>(api.post(`/portal/events/${eventId}/pings/${id}/ping`)),
  setPingMode: (eventId: string, mode: PingMode) => data<PingSetting>(api.put(`/portal/events/${eventId}/ping-mode`, { mode })),
  events: () => data<PortalEventRow[]>(api.get('/portal/events')),
  event: (id: string) => data<{ event: PortalEventDetail; view: ClientView['view'] }>(api.get(`/portal/events/${id}`)),
  setComment: (id: string, comment: string) => data<EventComment>(api.put(`/portal/events/${id}/comment`, { comment })),
  setDeviceNote: (eventId: string, itemId: string, note: string) => data<unknown>(api.put(`/portal/events/${eventId}/devices/${itemId}/note`, { note })),
  eventGraph: (eventId: string, from: Date, to: Date) => data<EventGraph>(api.get(`/portal/events/${eventId}/graph`, { params: { from: from.toISOString(), to: to.toISOString() } })),
  device: (eventId: string, itemId: string) => data<ClientDeviceView>(api.get(`/portal/events/${eventId}/devices/${itemId}`)),
  deviceHistory: (eventId: string, itemId: string, period: ClientDevicePeriod) => data<ClientDeviceHistory>(api.get(`/portal/events/${eventId}/devices/${itemId}/history`, { params: { period } })),
  deviceClients: (eventId: string, itemId: string) => data<ClientDeviceClient[]>(api.get(`/portal/events/${eventId}/devices/${itemId}/clients`)),
  people: () => data<ClientPerson[]>(api.get('/portal/people')),
  invite: (body: ClientInvite) => data<ClientPerson & { emailSent: boolean }>(api.post('/portal/people', body)),
  update: (id: string, body: { onlyDuringEvent?: boolean }) => data<ClientPerson>(api.put(`/portal/people/${id}`, body)),
  remove: (id: string) => data<null>(api.delete(`/portal/people/${id}`)),
}
